<script setup lang="ts">
import { computed, onMounted, ref, watch, type PropType } from 'vue';
import type { UserInDb } from '@/api';
import GaiaSearchInput from '../../base/inputs/GaiaSearchInput.vue';
import GaiaPickerOption from '../../composite/GaiaPickerOption.vue';
import GaiaEmptyState from '../../composite/GaiaEmptyState.vue';
import GaiaPagination from '../../composite/GaiaPagination.vue';
import GaiaSpinner from '../../base/GaiaSpinner.vue';
import { PermissionsService } from '@/services/permissionsService';
import type { GaiaPickedUser } from './types';
import { initialsOf } from '@/utils/initials';

/**
 * Search the organisation's people and pick one (or several) from the results.
 *
 * The body of the reassign / share modals, which had each grown their own copy
 * of it: two of them were identical down to the `:deep(.n-list-item__main)`
 * CSS, and the file that came second says so in a comment.
 */
const props = defineProps({
  /** Selected uid, or uids when `multiple`. */
  value: {
    type: [String, Array] as PropType<string | string[] | null>,
    default: null,
  },
  multiple: {
    type: Boolean,
    default: false,
  },
  pageSize: {
    type: Number,
    default: 10,
  },
  searchPlaceholder: {
    type: String,
    default: 'Search users by name or email',
  },
  /** Uids that cannot be picked — a file's current owner, say. */
  disabledUids: {
    type: Array as PropType<string[]>,
    default: () => [],
  },
  /** Offer the size picker under the list. Off in a tight modal. */
  showPageSizePicker: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits<{
  'update:value': [value: string | string[] | null];
  /** The row just picked, so a caller does not have to look the user up again. */
  pick: [user: GaiaPickedUser];
}>();

const query = ref('');
const page = ref(1);
const size = ref(props.pageSize);
const users = ref<UserInDb[]>([]);
const total = ref(0);
const loading = ref(false);
const error = ref<string | null>(null);

const selectedUids = computed<string[]>(() => {
  if (props.value === null) return [];
  return Array.isArray(props.value) ? props.value : [props.value];
});

async function load(): Promise<void> {
  loading.value = true;
  error.value = null;
  try {
    const result = await PermissionsService.searchUsers({
      text: query.value.trim(),
      page: page.value,
      pageSize: size.value,
      sortKey: 'email',
      sortDirection: 'asc',
    });
    users.value = result.results;
    total.value = result.result_count;
  } catch (error_) {
    error.value = error_ instanceof Error ? error_.message : 'Failed to load users';
    users.value = [];
    total.value = 0;
  } finally {
    loading.value = false;
  }
}

onMounted(() => void load());
watch([page, size], () => void load());

// A new term restarts at page 1, and the field fires this immediately on clear.
function applySearch(): void {
  if (page.value === 1) {
    void load();
  } else {
    page.value = 1; // the watcher reloads
  }
}

function isDisabled(user: UserInDb): boolean {
  return props.disabledUids.includes(user.uid);
}

function pick(user: UserInDb): void {
  if (isDisabled(user)) return;
  if (props.multiple) {
    const next = selectedUids.value.includes(user.uid)
      ? selectedUids.value.filter(uid => uid !== user.uid)
      : [...selectedUids.value, user.uid];
    emit('update:value', next);
  } else {
    // Single select stays picked once picked: these modals gate their primary
    // action on having a selection, so un-picking by re-clicking would only
    // ever disable the button the user is reaching for.
    emit('update:value', user.uid);
  }
  emit('pick', user);
}

/** "Nothing matches" reads differently from "nobody here" — the term decides. */
const emptyTitle = computed(() =>
  query.value.trim() ? 'No users match your search' : 'No users in this organisation'
);

/** Reset to the top when the caller reopens the modal around this list. */
function reset(): void {
  query.value = '';
  page.value = 1;
  void load();
}

defineExpose({ reset });
</script>

<template>
  <n-flex vertical :size="10" :wrap="false">
    <gaia-search-input
      v-model:value="query"
      variant="panel"
      size="medium"
      :placeholder="props.searchPlaceholder"
      @search="applySearch"
    />

    <gaia-empty-state v-if="error" :title="error" />

    <!-- The spinner replaces the list rather than overlaying it: a half-faded
         list of the previous page's people invites a click on the wrong row. -->
    <gaia-spinner v-else-if="loading" :style="{ margin: '28px auto' }" />

    <gaia-empty-state v-else-if="users.length === 0" :title="emptyTitle" />

    <template v-else>
      <n-flex vertical :size="0" :wrap="false">
        <gaia-picker-option
          v-for="user in users"
          :key="user.uid"
          :label="user.full_name || user.email"
          :meta="user.full_name ? user.email : undefined"
          :initials="initialsOf(user.full_name || user.email)"
          :selected="selectedUids.includes(user.uid)"
          :disabled="isDisabled(user)"
          :checkbox="props.multiple"
          @select="pick(user)"
        />
      </n-flex>

      <gaia-pagination
        v-if="total > size"
        :page="page"
        :page-size="size"
        :total="total"
        :page-sizes="props.showPageSizePicker ? [5, 10, 20, 50] : []"
        @update:page="(value: number) => (page = value)"
        @update:page-size="(value: number) => (size = value)"
      />
    </template>
  </n-flex>
</template>
